import { memo, useState } from 'react';
import { Handle, Position, NodeResizer, useReactFlow } from 'reactflow';
import { ChevronDown, ChevronRight, Box, Power } from 'lucide-react';
import { cn } from '@/lib/utils';

const ContainerNode = ({ id, data, selected }: any) => {
    const [collapsed, setCollapsed] = useState(false);
    const { setNodes } = useReactFlow();

    const handleToggleCollapse = (e: React.MouseEvent) => {
        e.stopPropagation();
        const newCollapsed = !collapsed;
        setCollapsed(newCollapsed);

        // Update data for persistence
        setNodes((nodes) => {
            // first map to update container
            const nodesWithUpdatedContainer = nodes.map((n) => {
                if (n.id === id) {
                    let newStyle = { ...n.style };
                    let newData = { ...n.data, isCollapsed: newCollapsed };

                    if (newCollapsed) {
                        // Collapsing: Save current size
                        newData.preCollapseSize = {
                            width: n.style?.width,
                            height: n.style?.height
                        };
                        newStyle = { ...newStyle, width: 200, height: 60 };
                    } else {
                        // Expanding: Restore size
                        const saved = n.data.preCollapseSize;
                        if (saved) {
                            newStyle = { ...newStyle, width: saved.width, height: saved.height };
                        } else {
                            // If no saved size (first time or reset), clear explicit w/h to use CSS defaults
                            newStyle.width = undefined;
                            newStyle.height = undefined;
                        }
                    }

                    return {
                        ...n,
                        data: newData,
                        style: newStyle
                    };
                }
                return n;
            });

            // then map to update children visibility
            return nodesWithUpdatedContainer.map(n => {
                if (n.parentNode === id) {
                    return { ...n, hidden: newCollapsed };
                }
                return n;
            });
        });
    };

    const handleToggleDisable = (e: React.MouseEvent) => {
        e.stopPropagation();
        const newDisabled = !data.isDisabled;

        setNodes((nodes) => nodes.map((n) => {
            if (n.id === id) {
                return { ...n, data: { ...n.data, isDisabled: newDisabled } };
            }
            return n;
        }));
    };

    return (
        <div className={cn(
            "rounded-lg border-2 transition-all group relative",
            // Transparency
            selected ? "border-blue-500 shadow-md bg-blue-50/10" : "border-slate-300 border-dashed hover:border-slate-400 bg-slate-50/10",
            data.isDisabled ? "opacity-70 bg-gray-100/50" : "",
            // Restore min-w/h for expanded state to prevent shrinking to 0 if style is undefined
            collapsed ? "w-[200px] h-[60px] bg-slate-100" : "min-w-[300px] min-h-[300px] h-full w-full"
        )}>
            {/* Resizer - Moved to be explicit and removed 'isVisible' constraint for debug if needed, but kept for now. 
                Increased handle size and added style for visibility. 
            */}
            {!collapsed && (
                <NodeResizer
                    minWidth={100}
                    minHeight={100}
                    isVisible={selected}
                    lineClassName="border-blue-400"
                    handleClassName="h-4 w-4 bg-blue-500 border-2 border-white rounded shadow-sm"
                    handleStyle={{ zIndex: 100 }} // Ensure handles are on top
                />
            )}

            {/* Header */}
            <div className={cn(
                "flex items-center justify-between px-3 py-2 border-b transition-colors rounded-t-lg backdrop-blur-sm", // Keep header readable
                data.isDisabled ? "bg-gray-200/80 border-gray-300" : (selected ? "bg-blue-50/80 border-blue-200" : "bg-slate-100/80 border-slate-200")
            )}>
                <div className="flex items-center gap-2 overflow-hidden">
                    <button
                        onClick={handleToggleCollapse}
                        className="hover:bg-slate-200 rounded p-0.5 text-slate-500 transition-colors"
                    >
                        {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                    <Box className="h-4 w-4 text-slate-500" />
                    <span className="text-sm font-semibold text-slate-700 truncate max-w-[120px]" title={data.label}>
                        {data.label || "Container"}
                    </span>
                </div>

                <div className="flex items-center gap-1">
                    {/* Disable Toggle */}
                    <button
                        onClick={handleToggleDisable}
                        className={cn(
                            "p-1 rounded transition-all",
                            data.isDisabled ? "text-red-600 bg-red-100 hover:bg-red-200" : "text-slate-400 hover:text-green-600 hover:bg-green-50"
                        )}
                        title={data.isDisabled ? "Enable Container" : "Disable Container"}
                    >
                        <Power className="h-3.5 w-3.5" />
                    </button>
                </div>
            </div>

            {/* Dependency Handles - Only allow connections to Container, not data flow specifically logic dependency */}
            {/* Top Handle for Incoming Dependencies */}
            <Handle
                type="target"
                position={Position.Top}
                className="!w-3 !h-3 !bg-slate-400 border-2 border-white"
                id="dep-in"
            />

            {/* Bottom Handle for Outgoing Dependencies */}
            <Handle
                type="source"
                position={Position.Bottom}
                className="!w-3 !h-3 !bg-slate-400 border-2 border-white"
                id="dep-out"
            />

            {/* Content Area (Only Visible when Expanded) */}
            {/* Conditional Placeholder: Only show if NOT hasChildren */}
            {!collapsed && !data.hasChildren && (
                <div className="p-4 text-xs text-slate-400 italic text-center mt-10 pointer-events-none">
                    Drag nodes here...
                </div>
            )}

            {/* Disabled Overlay */}
            {data.isDisabled && (
                <div className="absolute inset-0 bg-gray-100/50 backdrop-blur-[1px] rounded-lg z-10 flex items-center justify-center pointer-events-none">
                    <div className="bg-white/80 px-2 py-1 rounded text-[10px] font-bold text-gray-500 uppercase border border-gray-200 shadow-sm">
                        Disabled
                    </div>
                </div>
            )}
        </div>
    );
};

export default memo(ContainerNode);
